import { displayEntityName } from '@brand-lolly/app/social-media-influencer/modules/social-listening/utils/display-entity-name.util';
import { ReportDateRange } from '../models/preview/preview.models';
import {
	ReportBlockData,
	ReportBlockEntity,
	ReportEntityKind
} from './report-blocks.service';
import { ReportDataSource } from './report-settings.service';
import { ReportHome } from './report-home';
import {
	PREVIOUS_PERIOD_LABEL,
	ReportScopeFilter,
	scopeFilters,
	scopeGrouping,
	scopePreviousRange,
	scopeRange
} from './report-block-data';
import {
	hasReportMessage,
	reportDateLocale,
	reportMessage,
	reportMessageLanguage,
	sentenceStart
} from './report-messages';
import { formatUtcSpan, utcDayMonth } from './report-period';

/**
 * Where a number on a report came from: whose it is, what was counted, how a
 * keyword hit was matched and for which window.
 *
 * Every slide and every added chart prints this as a one-line footnote, with
 * the full detail on hover, so a reader can check any figure against the
 * module it was read from. It is DESCRIBED here and never looked up: every
 * value comes from what the page already used to make its reads, so the line
 * cannot name a window or a brand the numbers were not read for.
 */
export interface ReportSource {
	/** Whose numbers these are: the report's brand, or each entity compared. */
	entities: ReportSourceEntity[];
	/** What was counted, e.g. "Sentiment over time". */
	measure: string;
	/**
	 * The window the numbers were read for — the very instants every read on
	 * the page carries, not a restatement of the period. A chart with a period
	 * of its own names that one, as its read reported it.
	 */
	range: ReportDateRange;
	/** What a chart with filters of its own counted only: "TikTok, X". */
	filters?: ReportScopeFilter[];
	/** How its days were added up, when not day by day: "weekly". */
	grouping?: string | null;
	/** The window of the period set beside this one, when there is one. */
	previousRange?: ReportDateRange | null;
	origin?: ReportSourceOrigin | null;
	/**
	 * The module a section counting no mention is kept in; the line names it
	 * in place of the brand's, with no match type. Absent for every read of
	 * mentions.
	 */
	module?: ReportSourceModule | null;
	language?: string | null;
}

/**
 * Where a section counting no mention is read from — the brand's alerts on
 * its Notifications tab, the ratings on its Reviews tab. Nothing in it was
 * matched against a keyword, so its line carries no match type.
 */
export type ReportSourceModule = 'notifications' | 'reviews';

// CONTRACT: the backend's sections kept under the brand (`keyed_by` in
// reports/blocks/catalogue.py), by catalogue id.
const BLOCK_MODULES: Readonly<Record<string, ReportSourceModule>> = {
	alerts_fired: 'notifications',
	review_ratings: 'reviews'
};

export interface ReportSourceEntity {
	kind: ReportEntityKind;
	/** Null while the report brand's own name is still being read. */
	label: string | null;
}

/**
 * The report's own data source (`settings.source`), for a chart drawn about
 * it: "Competitors · Nike rivals", "Industry · Coffee". Named by the report,
 * which saved the name when it was made, rather than by the entities the chart
 * happens to hold.
 */
export interface ReportSourceOrigin {
	module: 'Competitors' | 'Industry';
	label: string;
}

export interface ReportSectionAbout {
	dataSource: ReportDataSource;
	// CONTRACT: empty for a source of one entity — its answer names nobody
	// (`sourceSectionEntities`).
	entities: string[];
}

/** The source of one fixed section, or null for one that shows no numbers
 *  (the cover) or that this list does not know. */
export function sectionSource(
	sectionId: string,
	brandLabel: string | null,
	range: ReportDateRange | null | undefined,
	language?: string | null,
	about?: ReportSectionAbout | null
): ReportSource | null {
	const measure = `section.measure.${sectionId}`;
	if (!hasReportMessage(measure) || !range) {
		return null;
	}
	if (about) {
		return {
			entities: aboutEntities(about),
			measure: reportMessage(language, measure),
			range,
			origin: originOf(about.dataSource),
			language
		};
	}
	return {
		entities: [{ kind: 'brand', label: brandLabel }],
		measure: reportMessage(language, measure),
		range,
		language
	};
}

function aboutEntities(about: ReportSectionAbout): ReportSourceEntity[] {
	const { dataSource } = about;
	if (dataSource.kind === 'industry') {
		return [
			{
				kind: 'industry',
				label: displayEntityName(dataSource.label) || dataSource.label
			}
		];
	}
	return about.entities.map(name => ({
		kind: 'competitor',
		label: displayEntityName(name) || name
	}));
}

/** What a fixed section shows, as a phrase inside a sentence. */
export function sectionReads(
	sectionId: string,
	fallback: string,
	language?: string | null
): string {
	const reads = `section.reads.${sectionId}`;
	return hasReportMessage(reads)
		? reportMessage(language, reads)
		: fallback.toLowerCase();
}

/**
 * The source of an added block.
 *
 * Whose numbers they are comes from the block's SPEC, because the data does
 * not say: a block drawn for one competitor alone looks exactly like one drawn
 * for the brand. No entities means the report's own brand — every block
 * written before comparison existed — or, on a report homed on a project or an
 * industry (`homeIsSource`), that home, which the line then names.
 *
 * What it was read over comes from the DATA when the chart has a read of its
 * own (`scope`): its own period, its filters, its grouping and the period set
 * beside it, as the server read them. `range` is the report's window, which
 * every other chart on the page was read over.
 */
export function blockSource(
	block: ReportBlockData | null | undefined,
	entities: ReportBlockEntity[] | null | undefined,
	brandLabel: string | null,
	range: ReportDateRange | null | undefined,
	dataSource: ReportDataSource | null = null,
	language?: string | null,
	homeIsSource = false
): ReportSource | null {
	if (!block || !range) {
		return null;
	}
	const named: ReportSourceEntity[] = entities?.length
		? entities.map(entity => ({
				kind: entity.kind,
				label: displayEntityName(entity.label) || null
		  }))
		: [];
	const againstTheBrand = REPORT_BLOCKS_AGAINST_THE_BRAND.has(block.block_id);
	// CONTRACT: the backend draws a spec naming nobody about the report's home
	// — a project's own competitors, or the industry — which is its source.
	const homeOrigin =
		homeIsSource && !named.length && dataSource
			? originOf(dataSource)
			: null;
	const whose: ReportSourceEntity[] = homeOrigin
		? []
		: !named.length || againstTheBrand
		? [{ kind: 'brand', label: brandLabel }, ...named]
		: named;
	const measure = `block.measure.${block.block_id}`;
	const source: ReportSource = {
		entities: whose,
		measure: hasReportMessage(measure)
			? reportMessage(language, measure)
			: block.title || block.block_id,
		range,
		origin:
			homeOrigin ??
			(againstTheBrand ? null : sourceOrigin(dataSource, entities)),
		module: BLOCK_MODULES[block.block_id] ?? null,
		language
	};
	const scope = block.scope;
	if (!scope) {
		return source;
	}
	return {
		...source,
		range: scopeRange(scope, range),
		filters: scopeFilters(scope, language),
		grouping: scopeGrouping(scope, language),
		previousRange: scopePreviousRange(scope)
	};
}

/**
 * The source of ONE number read from `source`: the same read, measuring only
 * that figure, and — when the figure belongs to one of several compared
 * entities — only that entity. A figure of the period set beside this one was
 * read over that period's window, and says so.
 */
export function numberSource(
	source: ReportSource,
	label: string,
	entityLabel?: string | null
): ReportSource {
	const shown = entityLabel ? displayEntityName(entityLabel) : '';
	const own = shown
		? source.entities.filter(entity => entity.label === shown)
		: [];
	const before =
		entityLabel === PREVIOUS_PERIOD_LABEL ? source.previousRange : null;
	return {
		...source,
		measure: label,
		entities: own.length ? own : source.entities,
		...(before ? { range: before, previousRange: null } : {})
	};
}

/**
 * "Source: Brand · Nike · Sentiment over time · exact match · 1 Sep – 30 Sep 2026",
 * and for a chart about the report's source "Source: Competitors · Nike rivals
 * · Adidas vs Puma · …" or "Source: Industry · Coffee · …".
 */
export function reportSourceLine(source: ReportSource): string {
	const language = source.language;
	const origin = source.origin;
	const names = source.entities
		.map(entity => entity.label)
		.filter(
			(label): label is string =>
				Boolean(label) && label !== origin?.label
		);
	const window = source.previousRange
		? reportMessage(language, 'source.window.versus', {
				current: formatReportWindow(source.range, language),
				previous: formatReportWindow(source.previousRange, language)
		  })
		: formatReportWindow(source.range, language);
	return [
		reportMessage(language, 'source.prefix', {
			module: sourceModuleName(source, language)
		}),
		origin?.label,
		names.join(reportMessage(language, 'source.names.versus')),
		source.measure,
		...(source.filters ?? []).map(filter => filter.value),
		source.grouping,
		source.module ? '' : reportMessage(language, 'source.exact'),
		window
	]
		.filter(Boolean)
		.join(' · ');
}

/** The same, one fact per line, for the tooltip. */
export function reportSourceDetail(source: ReportSource): string {
	const language = source.language;
	const origin = source.origin;
	return [
		reportMessage(language, 'source.detail.module', {
			module: sourceModuleName(source, language)
		}),
		origin ? originLine(origin, language) : '',
		origin?.module === 'Industry'
			? ''
			: entitiesLine(source.entities, language),
		reportMessage(language, 'source.detail.measure', {
			measure: source.measure
		}),
		...(source.filters ?? []).map(filter =>
			reportMessage(language, 'source.detail.filter', {
				name: filter.name,
				value: filter.value
			})
		),
		source.grouping
			? reportMessage(language, 'source.detail.grouped', {
					grouping: source.grouping
			  })
			: '',
		source.module ? '' : reportMessage(language, 'source.detail.match'),
		reportMessage(language, 'source.detail.range', {
			range: formatReportWindowDetail(source.range, language)
		}),
		source.previousRange
			? reportMessage(language, 'source.detail.compared_with', {
					range: formatReportWindowDetail(
						source.previousRange,
						language
					)
			  })
			: '',
		reportMessage(
			language,
			source.module
				? 'source.detail.live.' + source.module
				: 'source.detail.live'
		)
	]
		.filter(Boolean)
		.join('\n');
}

/**
 * The window as a reader reads it: "1 Sep – 30 Sep 2026", the year once when
 * both ends share it, "All time" for an all-time report.
 *
 * In UTC, like the report's own date label (`buildPreviewDateRange`), so the
 * two never disagree about which day a window starts on.
 */
export function formatReportWindow(
	range: ReportDateRange,
	language?: string | null
): string {
	if (range.period === 'all_time') {
		return reportMessage(language, 'window.all_time');
	}
	const start = new Date(range.start);
	const end = new Date(range.end);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
		return range.label;
	}
	return formatUtcSpan(start, end, date => dayMonth(date, language));
}

/**
 * One day as the report names it: "12 Sep" in English, the report's own
 * language's short form otherwise, in UTC like every window here. An ISO day
 * that does not parse is shown as it came.
 */
export function formatReportDay(day: string, language?: string | null): string {
	const date = new Date(`${day.slice(0, 10)}T00:00:00Z`);
	return Number.isNaN(date.getTime()) ? day : dayMonth(date, language);
}

/**
 * The report's source, when every entity the chart is about belongs to it: the
 * industry the report is about, or — in a report about a competitor project —
 * its competitors. Null otherwise, and the line names the entities instead.
 */
function sourceOrigin(
	dataSource: ReportDataSource | null,
	entities: ReportBlockEntity[] | null | undefined
): ReportSourceOrigin | null {
	if (!dataSource || !entities?.length) {
		return null;
	}
	const aboutTheSource =
		dataSource.kind === 'industry'
			? entities.every(
					entity =>
						entity.kind === 'industry' &&
						entity.id === dataSource.id
			  )
			: entities.every(entity => entity.kind === 'competitor');
	return aboutTheSource ? originOf(dataSource) : null;
}

/** "Competitors · Nike rivals", "Industry · Coffee": a report's data source as
 *  every surface names it — its source lines, its outline, the Reports list. */
export function dataSourceName(dataSource: ReportDataSource): string {
	const origin = originOf(dataSource);
	return `${origin.module} · ${origin.label}`;
}

/** A report's home the same way: "Brand · Nike", "Industry · Coffee". */
export function reportHomeName(
	home: Pick<ReportHome, 'kind' | 'label'>
): string {
	if (home.kind === 'brand') {
		const label = displayEntityName(home.label) || home.label;
		return label ? `Brand · ${label}` : 'Brand';
	}
	return dataSourceName({ kind: home.kind, id: '', label: home.label });
}

function originOf(dataSource: ReportDataSource): ReportSourceOrigin {
	return {
		module: dataSource.kind === 'industry' ? 'Industry' : 'Competitors',
		label: displayEntityName(dataSource.label) || dataSource.label
	};
}

function originLine(
	origin: ReportSourceOrigin,
	language: string | null | undefined
): string {
	return reportMessage(
		language,
		origin.module === 'Industry'
			? 'source.detail.industry'
			: 'source.detail.competitor_project',
		{ label: origin.label }
	);
}

/** The module a source line opens with: the one a section counting no
 *  mention is kept in, the report's source when the chart is about it, else
 *  whose entities these are. */
function sourceModuleName(
	source: ReportSource,
	language: string | null | undefined
): string {
	if (source.module) {
		return reportMessage(language, 'source.module.' + source.module);
	}
	if (!source.origin) {
		return sourceModule(source.entities, language);
	}
	return reportMessage(
		language,
		source.origin.module === 'Industry'
			? 'source.origin.industry'
			: 'source.origin.competitors'
	);
}

/**
 * CONTRACT: the blocks the backend's catalogue gives `against_brand` — read
 * for the report's own brand as well as the entity their spec names.
 */
const REPORT_BLOCKS_AGAINST_THE_BRAND: ReadonlySet<string> = new Set([
	'content_gaps'
]);

const MODULE_WORD_KEYS: Record<ReportEntityKind, [string, string]> = {
	brand: ['source.module.brand', 'source.module.brands'],
	competitor: ['source.module.competitor', 'source.module.competitors'],
	industry: ['source.module.industry', 'source.module.industries']
};

/** Brand, Competitor, Industry, or each kind a block compares. */
function sourceModule(
	entities: ReportSourceEntity[],
	language: string | null | undefined
): string {
	const words = (Object.keys(MODULE_WORD_KEYS) as ReportEntityKind[])
		.map(kind => {
			const count = entities.filter(
				entity => entity.kind === kind
			).length;
			return count
				? reportMessage(
						language,
						MODULE_WORD_KEYS[kind][count > 1 ? 1 : 0]
				  )
				: '';
		})
		.filter(Boolean);
	return sentenceStart(
		words.join(reportMessage(language, 'source.module.and')) ||
			reportMessage(language, 'source.module.brand')
	);
}

function entitiesLine(
	entities: ReportSourceEntity[],
	language: string | null | undefined
): string {
	const named = entities.filter(entity => entity.label);
	if (!named.length) {
		return '';
	}
	if (named.length === 1) {
		return reportMessage(language, `source.detail.${named[0].kind}`, {
			label: named[0].label ?? ''
		});
	}
	return reportMessage(language, 'source.detail.compared', {
		entities: named
			.map(entity =>
				reportMessage(language, 'source.detail.entity', {
					label: entity.label ?? '',
					kind: reportMessage(language, `source.kind.${entity.kind}`)
				})
			)
			.join(', ')
	});
}

/** The exact instants, so "the same window" can be checked, not trusted. */
function formatReportWindowDetail(
	range: ReportDateRange,
	language: string | null | undefined
): string {
	const start = new Date(range.start);
	const end = new Date(range.end);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
		return range.label;
	}
	if (range.period === 'all_time') {
		return reportMessage(language, 'source.detail.all_time', {
			end: stamp(end, language)
		});
	}
	return reportMessage(language, 'source.detail.window', {
		start: stamp(start, language),
		end: stamp(end, language)
	});
}

function dayMonth(date: Date, language: string | null | undefined): string {
	if (reportMessageLanguage(language) === 'en') {
		return utcDayMonth(date);
	}
	return new Intl.DateTimeFormat(reportDateLocale(language), {
		day: 'numeric',
		month: 'short',
		timeZone: 'UTC'
	}).format(date);
}

function stamp(date: Date, language: string | null | undefined): string {
	const hours = String(date.getUTCHours()).padStart(2, '0');
	const minutes = String(date.getUTCMinutes()).padStart(2, '0');
	return `${dayMonth(
		date,
		language
	)} ${date.getUTCFullYear()}, ${hours}:${minutes}`;
}
